/**
 * Left navigation - Unified Watchlist with company list and update badges
 * Supports drag-and-drop reordering via @dnd-kit
 */

import { useState, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { companyKeys } from '../../hooks/useCompanies';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanies } from '../../hooks/useCompanies';
import { useCompanyUpdates } from '../../hooks/useCompanyUpdates';
import { useUserRole } from '../../hooks/useUserRole';
import { KBStatusBadge } from '../company/KBStatusBadge';
import { UpdateBadge } from '../common/UpdateBadge';
import { NonViewerOnly } from '../common';
import { AddCompanyModal } from '../company/AddCompanyModal';
import { RemoveCompanyModal } from '../company/RemoveCompanyModal';
import { CompanyQuickView } from './CompanyQuickView';
import {
  deleteCompanyFromSystem,
  initiateWatchlistRemoval,
  confirmWatchlistRemoval,
  reorderWatchlist,
} from '../../services/api';
import { CompanyListItem, Materiality, WatchlistEntry } from '../../types';

// Company update info type (matches CompanyQuickView)
interface CompanyUpdateInfo {
  id: string;
  unread_count: number;
  highest_materiality: Materiality | null;
  latest_event: {
    title: string;
    event_type: string;
    published_at: string;
  } | null;
}

// Props for the sortable company row
interface SortableCompanyRowProps {
  company: CompanyListItem;
  isActive: boolean;
  isHovered: boolean;
  isExpanded: boolean;
  unreadCount: number;
  highestMateriality: Materiality | undefined;
  updateInfo: CompanyUpdateInfo | null;
  location: { pathname: string; search: string };
  getTickerDisplay: (company: CompanyListItem) => string;
  onToggle: () => void;
  onHover: (hovered: boolean) => void;
  onRemove: () => void;
  canRemove: boolean;
}

// Sortable company row component
function SortableCompanyRow({
  company,
  isActive,
  isHovered,
  isExpanded,
  unreadCount,
  highestMateriality,
  updateInfo,
  location,
  getTickerDisplay,
  onToggle,
  onHover,
  onRemove,
  canRemove,
}: SortableCompanyRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: company.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {/* Company Row */}
      <div
        className={`flex items-center ${
          isActive ? 'bg-primary-50 border-l-2 border-primary-600' : 'hover:bg-gray-50'
        }`}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
      >
        {/* Drag handle (visible on hover) */}
        <div
          {...attributes}
          {...listeners}
          className={`pl-1 py-2 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          } transition-opacity`}
          title="Drag to reorder"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </div>

        {/* Expand toggle */}
        <button
          onClick={onToggle}
          className="pl-1 pr-1 py-2 text-gray-400 hover:text-gray-600"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <svg
            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Company Link */}
        <Link
          to={`/companies/${company.id}`}
          className={`flex-1 flex items-center gap-2 py-2 pr-3 text-sm min-w-0 ${
            isActive ? 'text-primary-700 font-medium' : 'text-gray-700'
          }`}
        >
          <span className="truncate flex-1 min-w-0">
            {company.name}
            <span className="text-gray-400 font-normal">{getTickerDisplay(company)}</span>
          </span>
          <div className="flex-shrink-0 flex items-center gap-1.5">
            {company.kb_is_building && <KBStatusBadge isBuilding={true} completeness={0} />}
            <UpdateBadge count={unreadCount} highestMateriality={highestMateriality} />
          </div>
        </Link>

        {/* Remove button (visible on hover, users with permission only) */}
        {isHovered && canRemove && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-1.5 mr-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Remove from watchlist"
            data-testid={`remove-company-${company.id}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Quick View Panel on Hover */}
      {isHovered && (
        <CompanyQuickView
          company={company}
          updateInfo={updateInfo}
          onClose={() => onHover(false)}
        />
      )}

      {/* Expanded Content (Reports + Recent Updates) */}
      {isExpanded && (
        <div className="ml-7 border-l border-gray-200">
          {/* Recent Updates (only when unread > 0) */}
          {unreadCount > 0 && (
            <Link
              to={`/companies/${company.id}/updates`}
              className={`flex items-center justify-between px-3 py-1.5 text-sm ${
                location.pathname.includes(`/companies/${company.id}/updates`)
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                Recent Updates
              </span>
              <span className="text-xs text-gray-400">({unreadCount} new)</span>
            </Link>
          )}
          {/* Reports */}
          <Link
            to={`/companies/${company.id}?tab=reports`}
            className={`flex items-center justify-between px-3 py-1.5 text-sm ${
              location.search.includes('tab=reports') && isActive
                ? 'text-primary-600 bg-primary-50'
                : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Reports
            </span>
            {(company.report_count ?? 0) > 0 && (
              <span className="text-xs text-gray-400">({company.report_count})</span>
            )}
          </Link>
        </div>
      )}
    </div>
  );
}

export function LeftNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth();
  // Only fetch companies when authenticated to avoid 401 errors during login
  const { data: companies = [], isLoading: isCompaniesLoading } = useCompanies({
    enabled: isAuthenticated,
  });
  // PERF: updatesMap provides O(1) lookup vs O(n) array.find()
  const { updatesMap } = useCompanyUpdates();
  const { canRemoveCompany, isSystemAdmin } = useUserRole();
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
  const [hoveredCompanyId, setHoveredCompanyId] = useState<string | null>(null);
  const [removeEntry, setRemoveEntry] = useState<WatchlistEntry | null>(null);

  const toggleCompany = (companyId: string) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  const isCompanyActive = (companyId: string) => {
    return location.pathname.includes(`/companies/${companyId}`);
  };

  const getTickerDisplay = (company: CompanyListItem) => {
    if (company.ticker) {
      return ` (${company.ticker})`;
    }
    return '';
  };

  // Convert CompanyListItem to WatchlistEntry for the modal
  // In a real implementation, this would come from the watchlist API
  const toWatchlistEntry = (company: CompanyListItem): WatchlistEntry => ({
    company_id: company.id,
    company_name: company.name,
    company_type: company.company_type,
    ticker: company.ticker,
    domain: company.domain,
    // For now, assume personal unless we have group info
    source: 'personal',
    can_remove: true,
    added_at: new Date().toISOString(),
    kb_completeness: company.kb_completeness,
    kb_is_building: company.kb_is_building,
    unread_update_count: company.unread_update_count,
    highest_unread_materiality: company.highest_unread_materiality,
  });

  // Handle remove confirmation (watchlist removal)
  const handleRemoveConfirm = useCallback(
    async (companyId: string) => {
      // Step 1: Initiate removal
      await initiateWatchlistRemoval(companyId);

      // Step 2: Confirm removal
      await confirmWatchlistRemoval(companyId);

      // Refresh the companies list
      queryClient.invalidateQueries({ queryKey: companyKeys.list() });
    },
    [queryClient]
  );

  // Handle system delete (permanent deletion - system admin only)
  const handleSystemDelete = useCallback(
    async (companyId: string) => {
      const response = await deleteCompanyFromSystem(companyId);

      if (!response.deleted) {
        throw new Error(response.message || 'Failed to delete company');
      }

      // If we're currently viewing the deleted company, navigate away
      if (location.pathname.includes(`/companies/${companyId}`)) {
        navigate('/');
      }

      // Refresh the companies list
      queryClient.invalidateQueries({ queryKey: companyKeys.list() });
    },
    [location.pathname, navigate, queryClient]
  );

  // DnD sensors for drag-and-drop reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Company IDs for sortable context
  const companyIds = useMemo(() => companies.map((c) => c.id), [companies]);

  // Handle drag end - persist new order to backend
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      // Find the indices
      const oldIndex = companies.findIndex((c) => c.id === active.id);
      const newIndex = companies.findIndex((c) => c.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Use arrayMove to create the reordered array
      const reorderedCompanies = arrayMove(companies, oldIndex, newIndex);

      // Optimistically update the UI immediately
      queryClient.setQueryData(companyKeys.list(), reorderedCompanies);

      // Build the orders array for the API
      const orders = reorderedCompanies.map((company, index) => ({
        company_id: company.id,
        display_order: index,
      }));

      try {
        await reorderWatchlist(orders);
        // Invalidate to confirm server state matches
        queryClient.invalidateQueries({ queryKey: companyKeys.list() });
      } catch {
        // Revert to server state on error
        queryClient.invalidateQueries({ queryKey: companyKeys.list() });
      }
    },
    [companies, queryClient]
  );

  return (
    <>
      <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full">
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <Link to="/" className="flex items-center">
            <span className="text-xl font-bold text-primary-600">Dossier</span>
          </Link>
          <span className="text-xs text-gray-500 mt-1 block">Corporate Intelligence</span>
        </div>

        {/* Watchlist Section */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* Section Header */}
          <div className="px-4 mb-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Watchlist
            </span>
          </div>

          {/* Company List with Drag-and-Drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={companyIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {/* Show sign-in prompt for unauthenticated users */}
                {!isAuthLoading && !isAuthenticated ? (
                  <div className="px-4 py-3">
                    <p className="text-sm text-gray-500 mb-2">Sign in to view your watchlist</p>
                    <button
                      onClick={login}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-white bg-primary-600 hover:bg-primary-500 rounded-md transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                        />
                      </svg>
                      Sign in
                    </button>
                  </div>
                ) : isAuthLoading || isCompaniesLoading ? (
                  <div className="px-4 py-2 text-sm text-gray-400">Loading companies...</div>
                ) : companies.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-400">No companies tracked yet</div>
                ) : (
                  companies.map((company) => {
                    // PERF: O(1) Map lookup instead of O(n) array.find()
                    const updateInfo = updatesMap.get(company.id) ?? null;
                    const unreadCount =
                      updateInfo?.unread_count ?? company.unread_update_count ?? 0;
                    const highestMateriality = (updateInfo?.highest_materiality ??
                      company.highest_unread_materiality) as Materiality | undefined;

                    return (
                      <SortableCompanyRow
                        key={company.id}
                        company={company}
                        isActive={isCompanyActive(company.id)}
                        isHovered={hoveredCompanyId === company.id}
                        isExpanded={expandedCompanies.has(company.id)}
                        unreadCount={unreadCount}
                        highestMateriality={highestMateriality}
                        updateInfo={updateInfo}
                        location={location}
                        getTickerDisplay={getTickerDisplay}
                        onToggle={() => toggleCompany(company.id)}
                        onHover={(hovered) => setHoveredCompanyId(hovered ? company.id : null)}
                        onRemove={() => setRemoveEntry(toWatchlistEntry(company))}
                        canRemove={canRemoveCompany(false)}
                      />
                    );
                  })
                )}
              </div>
            </SortableContext>
          </DndContext>

          {/* Add Company Button (non-viewers only) */}
          <NonViewerOnly>
            <div className="mt-4 px-3">
              <button
                onClick={() => setIsAddCompanyOpen(true)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-md border border-dashed border-primary-300 transition-colors"
                data-testid="add-company-button"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Company
              </button>
            </div>
          </NonViewerOnly>
        </nav>

        {/* Settings Link */}
        <div className="border-t border-gray-200">
          <Link
            to="/settings"
            className={`flex items-center gap-2 px-4 py-3 text-sm ${
              location.pathname === '/settings'
                ? 'text-primary-600 bg-primary-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Settings
          </Link>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-400">dossier.fyi</div>
        </div>
      </aside>

      {/* Add Company Modal */}
      <AddCompanyModal isOpen={isAddCompanyOpen} onClose={() => setIsAddCompanyOpen(false)} />

      {/* Remove Company Modal */}
      <RemoveCompanyModal
        isOpen={removeEntry !== null}
        entry={removeEntry}
        onClose={() => setRemoveEntry(null)}
        onConfirm={handleRemoveConfirm}
        onSystemDelete={isSystemAdmin ? handleSystemDelete : undefined}
      />
    </>
  );
}

export default LeftNav;
